import { holidayService } from "@/api/holidays";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { Calendar as CalendarIcon } from "lucide-react";
import { useState } from "react";
import { CountryEnum } from "@/types";
import { formatCountry } from "@/utils/country";

export default function Calendar() {
  const { user } = useAuth();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const country = user?.country ?? CountryEnum.IN;

  const { data: holidays = [], isLoading } = useQuery({
    queryKey: ["holidays", selectedYear, country],
    queryFn: () => holidayService.getByYear(selectedYear, country),
  });

  const sortedHolidays = [...holidays].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold text-foreground">
            Holiday Calendar
          </h2>
          <p className="text-muted-foreground mt-1">
            View holidays for {formatCountry(country)} in the selected year
          </p>
        </div>

        <div className="flex gap-2">
          {[currentYear, currentYear - 1, currentYear - 2].map((year) => (
            <Button
              key={year}
              variant={selectedYear === year ? "default" : "outline"}
              onClick={() => setSelectedYear(year)}
              className="w-24"
            >
              {year}
            </Button>
          ))}
        </div>

        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle>{selectedYear} Holidays</CardTitle>
            <CardDescription>
              {holidays.length} holidays scheduled
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-center text-muted-foreground py-8">
                Loading holidays...
              </p>
            ) : (
              <div className="space-y-3">
                {sortedHolidays.map((holiday) => (
                  <div
                    key={holiday.id}
                    className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <CalendarIcon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">
                          {holiday.name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(holiday.date).toLocaleDateString("en-US", {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </p>
                      </div>
                    </div>
                    <Badge
                      variant={
                        holiday.type.toLowerCase() === "public"
                          ? "default"
                          : "secondary"
                      }
                    >
                      {holiday.type.toLowerCase() === "public"
                        ? "Public"
                        : "Optional"}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
